import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  TextField, MenuItem, Table, TableHead, TableBody, TableRow, TableCell,
  CircularProgress, Chip, Alert,
} from '@mui/material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const ALL = '__all__';
const OVERALL = '__overall__';

// A learner's full result history with filters: Year, Term, Assessment (the
// per-component "exam", or Overall = subject total) and Learning Area. Data is
// AssessmentScore docs (each = one learner+year+term+subject, holding the
// component scores), fetched fresh so the drilldown spans every year — not just
// the header's selected period.
export default function LearnerResultsDialog({ open, learner, onClose }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [fYear, setFYear] = useState(ALL);
  const [fTerm, setFTerm] = useState(ALL);
  const [fArea, setFArea] = useState(ALL);
  const [fAssessment, setFAssessment] = useState(OVERALL);

  useEffect(() => {
    if (!open || !learner?.admNo) return;
    setLoading(true); setError('');
    setFYear(ALL); setFTerm(ALL); setFArea(ALL); setFAssessment(OVERALL);
    axios.get(`${API}/api/academics/class-teacher/learner/${learner.admNo}/results`, { headers: hdrs() })
      .then(({ data }) => setDocs(data.results || []))
      .catch(err => setError(err.response?.data?.error || 'Failed to load results'))
      .finally(() => setLoading(false));
  }, [open, learner]);

  const years = useMemo(() => [...new Set(docs.map(d => d.academicYear))].sort().reverse(), [docs]);
  const terms = useMemo(() => [...new Set(docs.map(d => d.term))].sort(), [docs]);
  const areas = useMemo(() => [...new Set(docs.map(d => d.learningAreaName))].sort(), [docs]);
  // Distinct component ("assessment") names across all the learner's subjects.
  const assessments = useMemo(() => {
    const s = new Set();
    docs.forEach(d => (d.scores || []).forEach(ln => ln.componentName && s.add(ln.componentName)));
    return [...s].sort();
  }, [docs]);

  // Docs passing the year/term/area filters.
  const filteredDocs = useMemo(() => docs.filter(d =>
    (fYear === ALL || d.academicYear === fYear) &&
    (fTerm === ALL || String(d.term) === String(fTerm)) &&
    (fArea === ALL || d.learningAreaName === fArea)
  ), [docs, fYear, fTerm, fArea]);

  // Rows: Overall = one row per subject doc (its total + grade); a specific
  // assessment = that component's mark within each matching subject doc.
  const rows = useMemo(() => {
    if (fAssessment === OVERALL) {
      return filteredDocs.map(d => ({
        key: `${d._id}`, year: d.academicYear, term: d.term, area: d.learningAreaName,
        assessment: 'Overall', score: d.totalRaw, max: d.totalMax,
        pct: d.percentage, grade: d.grade,
      }));
    }
    const out = [];
    filteredDocs.forEach(d => {
      (d.scores || []).forEach(ln => {
        if (ln.componentName !== fAssessment) return;
        if (ln.score === null || ln.score === undefined) return;
        out.push({
          key: `${d._id}-${ln.componentName}`, year: d.academicYear, term: d.term,
          area: d.learningAreaName, assessment: ln.componentName,
          score: ln.score, max: ln.maxScore,
          pct: ln.maxScore ? +((ln.score / ln.maxScore) * 100).toFixed(2) : 0, grade: null,
        });
      });
    });
    return out;
  }, [filteredDocs, fAssessment]);

  const mean = useMemo(() => rows.length
    ? +(rows.reduce((a, r) => a + (r.pct || 0), 0) / rows.length).toFixed(1) : null, [rows]);

  const showGrade = fAssessment === OVERALL;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {learner?.name} — Results
        {learner?.admNo && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{learner.admNo}</Typography>}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? <CircularProgress /> : (
          <>
            <Box display="flex" gap={1.5} flexWrap="wrap" mb={2}>
              <TextField select size="small" label="Year" value={fYear} onChange={e => setFYear(e.target.value)} sx={{ minWidth: 120 }}>
                <MenuItem value={ALL}>All years</MenuItem>
                {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Term" value={fTerm} onChange={e => setFTerm(e.target.value)} sx={{ minWidth: 110 }}>
                <MenuItem value={ALL}>All terms</MenuItem>
                {terms.map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Learning Area" value={fArea} onChange={e => setFArea(e.target.value)} sx={{ minWidth: 180 }}>
                <MenuItem value={ALL}>All learning areas</MenuItem>
                {areas.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Assessment / Exam" value={fAssessment} onChange={e => setFAssessment(e.target.value)} sx={{ minWidth: 180 }}>
                <MenuItem value={OVERALL}>Overall (subject total)</MenuItem>
                {assessments.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </TextField>
            </Box>

            {mean != null && (
              <Box mb={1}>
                <Chip size="small" color="primary" label={`${rows.length} record(s) · mean ${mean}%`} />
              </Box>
            )}

            {rows.length === 0 ? (
              <Typography color="text.secondary">No results match these filters.</Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 520 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Year</strong></TableCell>
                      <TableCell align="center"><strong>Term</strong></TableCell>
                      <TableCell><strong>Learning Area</strong></TableCell>
                      {!showGrade && <TableCell><strong>Assessment</strong></TableCell>}
                      <TableCell align="center"><strong>Score</strong></TableCell>
                      <TableCell align="center"><strong>%</strong></TableCell>
                      {showGrade && <TableCell align="center"><strong>Grade</strong></TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(r => (
                      <TableRow key={r.key} hover>
                        <TableCell>{r.year}</TableCell>
                        <TableCell align="center">{r.term}</TableCell>
                        <TableCell>{r.area}</TableCell>
                        {!showGrade && <TableCell>{r.assessment}</TableCell>}
                        <TableCell align="center">{r.score}/{r.max}</TableCell>
                        <TableCell align="center">{typeof r.pct === 'number' ? r.pct.toFixed(2) : r.pct}%</TableCell>
                        {showGrade && <TableCell align="center"><Chip size="small" label={r.grade || '—'} /></TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
